import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { formatJMD, listExpenses, listIncome } from '../lib/finances';
import { listTodos } from '../lib/todos';
import { listPurchaseOrders } from '../lib/purchaseOrders';
import { listAssets } from '../lib/marketing';
import { listAllBatches } from '../lib/formulas';
import { listInvoices } from '../lib/invoices';
import { listStatements } from '../lib/stockist';
import { resolvePeriod } from '../lib/period';
import { formatDueLabel } from '../lib/todos';
import { todayISO } from '../lib/dates';
import {
  batchUnits,
  buildActivity,
  buildAttention,
  changeVs,
  formatTime,
  goalProgress,
  groupActivityByDay,
  invoiceAttention,
  latestBatch,
  moneyOwed,
  moneySummary,
  revenueThisYear,
} from '../lib/overview';
import {
  CalendarIcon,
  CheckCircleIcon,
  FlaskIcon,
  InstagramIcon,
  InvoiceIcon,
  SparkleIcon,
} from '../components/icons';
import './Dashboard.css';

// Kept short deliberately: the Overview is a launch point, not a report.
const PERIOD_OPTIONS = ['This month', 'Last month', 'This quarter', 'Year to date'];

function Delta({ change, goodWhenUp = true }) {
  if (!change) return <span className="ov-delta muted">No earlier period to compare</span>;
  const good = change.up === goodWhenUp;
  return (
    <span className={'ov-delta ' + (good ? 'good' : 'bad')}>
      <span aria-hidden="true">{change.up ? '↑' : '↓'}</span>
      vs {formatJMD(change.previous)} last period
    </span>
  );
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [periodName, setPeriodName] = useState('This month');

  const today = useMemo(() => new Date(), []);
  const period = useMemo(() => resolvePeriod(periodName, { today }), [periodName, today]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [income, expenses, todos, purchaseOrders, marketingAssets, batches, invoices] =
          await Promise.all([
            listIncome(),
            listExpenses(),
            listTodos(),
            listPurchaseOrders(),
            listAssets(),
            listAllBatches(),
            // Contained like the balance sheet on Accounting: if migrations
            // 0029/0030 haven't been run, the invoice cards go quiet rather
            // than blanking the whole Overview.
            listInvoices().catch(() => []),
          ]);
        const statementsByInvoice = await listStatements(
          invoices.filter((i) => i.kind === 'Stockist').map((i) => i.id)
        ).catch(() => ({}));

        if (!cancelled) {
          setData({
            income,
            expenses,
            todos,
            purchaseOrders,
            marketingAssets,
            batches,
            invoices,
            statementsByInvoice,
          });
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <p className="ov-loading">Loading…</p>;
  if (error) return <p className="ov-error">Couldn't load the overview: {error}</p>;
  if (!data) return null;

  const money = moneySummary(data.income, data.expenses, period);
  const goal = goalProgress(revenueThisYear(data.income, today));
  const attention = buildAttention(data, today);
  const activity = groupActivityByDay(buildActivity(data), today);
  const todayIso = todayISO(today);
  const owed = moneyOwed(data.invoices, data.statementsByInvoice, todayIso);
  const invoiceItems = invoiceAttention(data.invoices, data.statementsByInvoice, todayIso);
  const batch = latestBatch(data.batches);
  const units = batchUnits(batch);
  const todayStr = todayISO(today);

  return (
    <div className="ov-page">
      <PageHeader
        title={
          <>
            Overview
            <SparkleIcon className="page-title-sparkle" />
          </>
        }
        subtitle="A snapshot of what's happening across the business."
        actions={
          <label className="ov-period">
            <CalendarIcon />
            <select value={periodName} onChange={(e) => setPeriodName(e.target.value)}>
              {PERIOD_OPTIONS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </label>
        }
      />

      {/* ---- money: three equal cards, plus the goal as support ---------- */}
      <section className="ov-money">
        <article className="ov-card ov-stat">
          <span className="ov-stat-icon income" aria-hidden="true">↗</span>
          <h3>Income</h3>
          <p className="ov-stat-value">{formatJMD(money.income)}</p>
          <Delta change={changeVs(money.income, money.previous?.income)} />
        </article>

        <article className="ov-card ov-stat">
          <span className="ov-stat-icon expense" aria-hidden="true">↘</span>
          <h3>Expenses</h3>
          <p className="ov-stat-value">{formatJMD(money.expenses)}</p>
          {/* Spending more is not an improvement, so the arrow's colour flips. */}
          <Delta change={changeVs(money.expenses, money.previous?.expenses)} goodWhenUp={false} />
        </article>

        <article className="ov-card ov-stat">
          <span className="ov-stat-icon net" aria-hidden="true">◎</span>
          <h3>Net income</h3>
          <p className={'ov-stat-value' + (money.net < 0 ? ' negative' : '')}>{formatJMD(money.net)}</p>
          <Delta change={changeVs(money.net, money.previous?.net)} />
        </article>

        <article className="ov-card ov-goal">
          <h3>{today.getFullYear()} revenue goal</h3>
          <p className="ov-goal-figure">
            <strong>{formatJMD(goal.revenue)}</strong>
            <span> of {formatJMD(goal.goal)}</span>
          </p>
          <div
            className="ov-goal-bar"
            role="progressbar"
            aria-valuenow={Math.round(goal.pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span style={{ width: `${goal.barPct}%` }} />
          </div>
          <p className="ov-goal-pct">{goal.pct.toFixed(1)}% of goal</p>
        </article>

        {/* Money you're owed sits beside the goal, not among the three stat
            cards — those report what actually moved, and this hasn't. The two
            figures stay separate for the same reason: sold-and-unpaid is a
            real claim on someone, stock in a shop is only inventory. */}
        <article className="ov-card ov-owed">
          <h3>Owed to you</h3>
          <p className="ov-owed-value">{formatJMD(owed.due)}</p>
          <p className="ov-owed-note">Sold or invoiced, not yet paid</p>
          <div className="ov-owed-split">
            <span>
              <strong>{formatJMD(owed.stockOut)}</strong>
              <em>
                stock out
                {owed.stockOutUnits > 0 ? ` · ${owed.stockOutUnits} units` : ''}
              </em>
            </span>
            <Link to="/invoices" className="ov-owed-link">
              Invoices →
            </Link>
          </div>
        </article>
      </section>

      <div className="ov-columns">
        {/* ---- needs attention: real items, each with a way in ----------- */}
        <section className="ov-card ov-attention">
          <header className="ov-card-head">
            <h2>Needs attention</h2>
          </header>

          <AttentionRow
            tone="task"
            icon={<CheckCircleIcon />}
            heading={
              attention.todos.total === 1 ? '1 open task' : `${attention.todos.total} open tasks`
            }
            empty="Nothing open — all clear."
            to="/todo"
            action="View all tasks"
            items={attention.todos.items.map((t) => ({
              key: t.id,
              text: t.title,
              meta: t.due_date ? formatDueLabel(t.due_date, todayStr) : null,
            }))}
          />

          {/* Money already earned but unpaid outranks everything else here —
              it's the one item on this page where waiting costs you. */}
          <AttentionRow
            tone="invoice"
            icon={<InvoiceIcon />}
            heading={
              invoiceItems.total === 1
                ? '1 invoice needs chasing'
                : `${invoiceItems.total} invoices need chasing`
            }
            empty="Every invoice is settled and reported."
            to="/invoices"
            action="View invoices"
            items={invoiceItems.items.map((item) => ({
              key: item.id,
              to: `/invoices/${item.invoiceId}`,
              text: item.amount ? `${item.label} — ${formatJMD(item.amount)}` : item.label,
              meta: item.meta,
            }))}
          />

          <AttentionRow
            tone="po"
            icon={<CalendarIcon />}
            heading={
              attention.purchaseOrders.total === 1
                ? '1 purchase order due soon'
                : `${attention.purchaseOrders.total} purchase orders due soon`
            }
            empty="Nothing arriving in the next week."
            to="/suppliers"
            action="View all POs"
            items={attention.purchaseOrders.items.map((po) => ({
              key: po.id,
              text: `${po.po_number} — ${po.vendor?.name || 'Vendor'}`,
              meta: po.expected_delivery_date
                ? formatDueLabel(po.expected_delivery_date, todayStr)
                : null,
            }))}
          />

          <AttentionRow
            tone="marketing"
            icon={<InstagramIcon />}
            heading={
              attention.marketing.total === 1
                ? '1 marketing item awaiting review'
                : `${attention.marketing.total} marketing items awaiting review`
            }
            empty="You're all caught up."
            to="/marketing"
            action="View marketing"
            items={attention.marketing.items.map((a) => ({
              key: a.id,
              // listAssets embeds the versions under their table name; the
              // latest version's caption is the human label for the post.
              text:
                a.marketing_asset_versions?.[a.marketing_asset_versions.length - 1]?.caption ||
                `${a.channel || 'Post'} awaiting review`,
              meta: a.channel,
            }))}
          />
        </section>

        {/* ---- latest batch ---------------------------------------------- */}
        <section className="ov-card ov-batch">
          <header className="ov-card-head">
            <span className="ov-round-icon batch" aria-hidden="true">
              <FlaskIcon />
            </span>
            <h2>Latest batch</h2>
          </header>

          {batch ? (
            <>
              <p className="ov-batch-name">
                {batch.formula?.family?.name || batch.formula?.product_name || 'Batch'}
              </p>
              {batch.batch_code && <p className="ov-batch-code">Batch #{batch.batch_code}</p>}
              <dl className="ov-batch-facts">
                <div>
                  <dt>Made</dt>
                  <dd>{batch.made_on ? formatDueLabel(batch.made_on, todayStr) : '—'}</dd>
                </div>
                <div>
                  <dt>Units</dt>
                  <dd>{units != null ? units : '—'}</dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="ov-empty">No batches logged yet.</p>
          )}

          <Link className="ov-link-button" to="/production-log">
            View production log <span aria-hidden="true">›</span>
          </Link>
        </section>
      </div>

      {/* ---- recent activity ---------------------------------------------- */}
      <section className="ov-card ov-activity">
        <header className="ov-card-head">
          <h2>Recent activity</h2>
        </header>

        {activity.length === 0 ? (
          <p className="ov-empty">Nothing recorded yet.</p>
        ) : (
          <div className="ov-activity-days">
            {activity.map((day) => (
              <div className="ov-activity-day" key={day.key}>
                <h4>{day.label}</h4>
                <ul>
                  {day.events.map((event, i) => (
                    <li key={`${day.key}-${i}`}>
                      <span className={'ov-dot ' + event.tone} aria-hidden="true" />
                      <span className="ov-activity-text">
                        <strong>{event.label}</strong>
                        {event.detail && <span> — {event.detail}</span>}
                      </span>
                      <time>{formatTime(event.at)}</time>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// One attention block: a count you can act on, up to three real examples, and
// the way through to the section that owns them.
function AttentionRow({ tone, icon, heading, items, empty, to, action }) {
  return (
    <div className="ov-attention-row">
      <span className={'ov-round-icon ' + tone} aria-hidden="true">
        {icon}
      </span>

      <div className="ov-attention-main">
        <p className={'ov-attention-heading ' + tone}>{heading}</p>
        {items.length === 0 ? (
          <p className="ov-attention-empty">{empty}</p>
        ) : (
          <ul>
            {items.map((item) => {
              const body = (
                <>
                  <span>{item.text}</span>
                  {item.meta && <span className="ov-attention-meta">{item.meta}</span>}
                </>
              );
              // An item can carry its own destination when there's something
              // more specific to open than the section — clicking "Devon House
              // owes J$12,500" should land on that consignment, not the list.
              return (
                <li key={item.key}>
                  {item.to ? (
                    <Link className="ov-attention-item-link" to={item.to}>
                      {body}
                    </Link>
                  ) : (
                    body
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Link className="ov-link-button" to={to}>
        {action} <span aria-hidden="true">›</span>
      </Link>
    </div>
  );
}
